/**
 * Перевод предложений: потолки вида и разбор вставленного текста в предложения
 * (2.1 и 2.2 `docs/translate-exercise-design.md`).
 *
 * ⚠️ Модуль ни от кого в пакете не зависит, и это не случайность: схемы в
 * `./exercises.ts` берут потолки отсюда, а обратный импорт дал бы цикл, в
 * котором схема читает константу до её инициализации (ровно та причина, по
 * которой `./column.ts` тоже листовой).
 *
 * Разбор живёт в корне пакета, а не за барьером `./exercises`: ответов в нём
 * нет, есть только русские предложения, которые ученик и так видит. Читает его
 * панель свойств заготовки на каждое нажатие клавиши.
 */

/**
 * Потолки вида.
 *
 *   TRANSLATE_MAX_ITEMS    предложений в одном задании и ключей в ответе. Ключей
 *                          больше, чем предложений, у честного ответа не бывает,
 *                          а лишний ключ ронял бы разбор всей выдачи архива
 *   TRANSLATE_ITEM_MAX     знаков в одном предложении. Длиннее не обрезается,
 *                          а помечается (пункт 8 разбора ниже)
 *   TRANSLATE_ANSWER_MAX   знаков в ответе на одно предложение. Режет по нему
 *                          поле ученика, отвергает схема: разойдясь, они дали
 *                          бы ответ, который набран, но не отправляется
 */
export const TRANSLATE_MAX_ITEMS = 40;
export const TRANSLATE_ITEM_MAX = 500;
export const TRANSLATE_ANSWER_MAX = 500;
/**
 * Знаков в поле «Что тренируем» (1.11 `docs/translate-ai-check-design.md`):
 * текст преподавателя, который едет модели вместе с предложениями. Без него
 * модель засчитала бы Präteritum в упражнении на Perfekt.
 */
export const TRANSLATE_FOCUS_MAX = 200;

/** Предложение задания: текст и свой идентификатор, по которому лежит ответ. */
export interface TranslateItem {
  id: string;
  text: string;
}

/**
 * Пометка подозрительной строки. Пометка не правит строку, а показывает её
 * человеку с кнопкой «Убрать»: угадывание здесь дороже вопроса.
 *
 *   header      кончается двоеточием, скорее всего заголовок задания
 *   latin       похоже, что вставлено вместе с ответом: два латинских слова
 *               подряд или латиница после разделителя, см. `LATIN_ANSWER`
 *   lowercase   начинается со строчной, а предыдущая строка оборвана на
 *               полуфразе: скорее всего хвост предыдущей строки
 *   too_long    длиннее `TRANSLATE_ITEM_MAX`: заготовка с такой строкой не
 *               сохраняется, пока её не разделят
 */
export type TranslateFlag = "header" | "latin" | "lowercase" | "too_long";

export interface TranslateParse {
  items: TranslateItem[];
  /** По id предложения его пометки. Предложения без пометок здесь нет вовсе. */
  flags: Record<string, TranslateFlag[]>;
  /** Сколько предложений не поместилось в `TRANSLATE_MAX_ITEMS` и отброшено. */
  dropped: number;
}

/**
 * Перевод строки во всех видах, что приезжают из буфера обмена: Windows, Unix,
 * старый Mac, юникодные разделители строки и абзаца (их кладут Word и PDF) и
 * перевод страницы, который приносит текст, скопированный из PDF через границу
 * страниц.
 */
const LINE_BREAK = /\r\n|\n|\r|\f|\u2028|\u2029/;

/** Табуляция и неразрывные пробелы, которые приносит вставка из документа. */
const SOFT_SPACE = /[\t\u00a0\u202f\u2007]+/g;

/**
 * Номер в начале строки. Порядок веток значим: скобочный и «№» проверяются
 * раньше голых цифр.
 *
 * ⚠️ Одна-две цифры со знаком это номер и без пробела после («1.Он»), а цифры
 * без знака это не номер («1945 год был»). Цифра сразу за знаком тоже отменяет
 * номер: «1.5 кг» это число, а не пятый пункт. Цифр в номере не больше двух у
 * всех веток одинаково: предложений в задании не больше сорока, и «(123)» или
 * «№123» это уже содержимое строки, а не номер.
 */
const NUMBERING: readonly RegExp[] = [
  /^\(\d{1,2}\)\s*/,
  /^№\s?\d{1,2}[.)]?(?:\s+|$)/,
  /^\d{1,2}[.)](?!\d)\s*/,
  /^[a-zа-яё]\)\s*/i,
];

/**
 * Маркер списка. ⚠️ Только с пробелом после: знак, приклеенный к слову, это
 * часть слова («-ся»). Снимается один, поэтому реплика диалога («– Где ты был?
 * – Дома.») теряет только первое тире.
 */
const MARKER = /^[-*•·–—]\s+/;

/** Тире реплики среди маркеров: у строки-продолжения оно остаётся при склейке. */
const DIALOGUE_DASH = /^[-–—]/;

/**
 * Хвост-заполнитель под ответ: подчёркивания и ряды точек, которые в
 * распечатке оставляют место для руки. Три точки это многоточие, заполнитель
 * начинается с четырёх.
 */
const FILLER_TAIL = /\s*(?:_{2,}|\.{4,}|…{2,})\s*$/;

/**
 * Строка, похожая на «предложение вместе с ответом». Два признака:
 *
 *   - два латинских слова подряд через пробел («He is a doctor»);
 *   - латиница после разделителя (тире с пробелом перед ним, «=» или «:»),
 *     которому предшествует кириллица («Он врач = Arzt»).
 *
 * ⚠️ Одно латинское слово внутри русской фразы («Enter между полями», «в IT»)
 * не помечается: в списке на перевод это обычное дело, и пометка на каждой
 * такой строке учила бы её не читать. Цена правила известна и принята: «Мне
 * нравится New York» помечается, хотя ответа там нет. Дефис внутри слова
 * («кто-то») разделителем не считается, поэтому у тире нужен пробел перед ним.
 */
const LATIN_ANSWER: readonly RegExp[] = [
  /(?<!\p{L})[A-Za-z]+(?:['’-][A-Za-z]+)*\s+[A-Za-z]/u,
  /\p{Script=Cyrillic}.*(?:\s[-–—]|=|:).*[A-Za-z]/u,
];
const LOWERCASE_START = /^\p{Ll}/u;
/**
 * Конец законченной фразы. Закрывающие кавычки и скобки после знака не
 * мешают: «Он сказал: "Да."» фразу закончил.
 */
const PHRASE_END = /[.!?…;:]["'»”’)\]]*$/;

interface Line {
  text: string;
  numbered: boolean;
  /**
   * Снятое тире реплики с пробелом, как было набрано. Нужно одной склейке: у
   * строки-продолжения («1. Он спросил:» и под ней «– Где ты был?») это часть
   * текста, а не маркер списка.
   */
  dash: string;
}

/** Пункты 2-4 разбора над одной строкой: пробелы, номер и маркер, хвост. */
function cleanLine(raw: string): Line {
  let text = raw.replace(SOFT_SPACE, " ").replace(/ {2,}/g, " ").trim();

  let numbered = false;
  for (const pattern of NUMBERING) {
    const match = pattern.exec(text);
    if (match === null) continue;
    text = text.slice(match[0].length);
    numbered = true;
    break;
  }
  let dash = "";
  const marker = MARKER.exec(text);
  if (marker !== null) {
    if (DIALOGUE_DASH.test(marker[0])) dash = `${marker[0].trimEnd()} `;
    text = text.slice(marker[0].length);
  }

  // Заполнитель бывает и в два слоя («_____ .....»), поэтому до неподвижной точки
  while (FILLER_TAIL.test(text)) text = text.replace(FILLER_TAIL, "");
  return { text: text.trim(), numbered, dash };
}

/**
 * Номер на отдельной строке («1.» и под ним текст) забирает следующую строку
 * без номера целиком: это одно пронумерованное предложение, а не две строки.
 * Делается ДО подсчёта доли нумерованных, иначе у списка, где каждый номер
 * стоит отдельно, половина строк выглядела бы ненумерованной и перенос из PDF
 * не склеивался бы никогда.
 */
function attachBareNumbers(lines: readonly Line[]): Line[] {
  const out: Line[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] as Line;
    const next = lines[i + 1];
    if (line.numbered && line.text === "" && next !== undefined && !next.numbered) {
      out.push({ text: next.text, numbered: true, dash: "" });
      i += 1;
      continue;
    }
    out.push(line);
  }
  return out;
}

/**
 * Пункт 6: перенос строки из PDF. Если больше половины строк несли номер,
 * строка без номера это хвост предыдущей и приклеивается к ней через пробел.
 * Без нумерации не склеивается ничего: у текста без номеров отличить перенос
 * от следующего предложения нечем.
 *
 * Строки приходят уже после `attachBareNumbers`: номер на отдельной строке
 * вместе со следующей считается одной нумерованной строкой. Тире реплики у
 * приклеиваемой строки остаётся: там оно часть текста. Первая строка без
 * номера (заголовок перед списком) приклеиваться некуда, она остаётся
 * отдельной.
 */
function mergeWrapped(lines: readonly Line[]): Line[] {
  const numbered = lines.filter((line) => line.numbered).length;
  if (numbered * 2 <= lines.length) return [...lines];

  const out: Line[] = [];
  for (const line of lines) {
    const last = out[out.length - 1];
    if (!line.numbered && last !== undefined) {
      const tail = `${line.dash}${line.text}`;
      last.text = last.text === "" ? tail : `${last.text} ${tail}`;
      continue;
    }
    out.push({ ...line });
  }
  return out;
}

/**
 * Короткий пункт списка слов: не больше трёх слов и без запятой («дом»,
 * «кошка», «раскладка на слайде»). После такой строки строчная буква следующей
 * это следующий пункт, а не хвост: у списка слов знаков в конце нет вовсе, и
 * без этой оговорки помечалась бы каждая строка, кроме первой. Запятая
 * оговорку снимает: «Он пришёл домой,» оборван на полуфразе при любой длине.
 */
function isShortItem(text: string): boolean {
  return !text.includes(",") && text.split(/\s+/).filter((word) => word !== "").length <= 3;
}

/**
 * Пометки строки. `previous` это предыдущая ОСТАВЛЕННАЯ строка, у первой её нет.
 */
function flagsOf(line: Line, previous: Line | undefined): TranslateFlag[] {
  const flags: TranslateFlag[] = [];
  if (line.text.endsWith(":")) flags.push("header");
  if (LATIN_ANSWER.some((pattern) => pattern.test(line.text))) flags.push("latin");
  // «Хвост предыдущей строки» бывает только там, где предыдущая оборвалась на
  // полуфразе (словом или запятой) и сама не похожа на пункт списка слов
  // (`isShortItem`). ⚠️ Строчная буква сама по себе ничего не
  // значит: простой список («раскладка на слайде;») целиком из строчных, и
  // прежнее правило помечало в нём каждую строку, включая первую, у которой
  // продолжать нечего. Пронумерованная строка не помечается никогда: хвостом
  // предыдущей она не бывает, строчная буква у неё это опечатка
  if (
    !line.numbered &&
    previous !== undefined &&
    !PHRASE_END.test(previous.text) &&
    !isShortItem(previous.text) &&
    LOWERCASE_START.test(line.text)
  ) {
    flags.push("lowercase");
  }
  if (line.text.length > TRANSLATE_ITEM_MAX) flags.push("too_long");
  return flags;
}

/**
 * Идентификаторы прежних предложений, в два прохода (2.2 проекта).
 *
 * Первый проход: строка, совпавшая по тексту с незанятой прежней, берёт её id.
 * Второй: оставшиеся берут id незанятой прежней на ТОЙ ЖЕ позиции (это правка
 * текста на месте), иначе новый.
 *
 * ⚠️ **Второй проход работает, только если строк столько же, сколько было.**
 * Позиция это довод лишь при правке на месте. Когда строк стало меньше или
 * больше, та же позиция указывает на чужое предложение: из [a, b, c] убрали
 * b и заодно поправили c, и c' встал на место b, то есть получил бы id b
 * вместе с ответом ученика на b. На уроке ответы уже лежат по id (10.4
 * `docs/translate-exercise-design.md`), поэтому новый id честнее: пустое поле
 * лучше чужого ответа под предложением.
 *
 * ⚠️ Однопроходный вариант (по позиции сразу) при вставке строки в середину
 * сдвигал все id ниже неё, а ответ лежит по id: у всех следующих предложений
 * ответы переехали бы к соседям. Посимвольный набор новой строки держится
 * вторым проходом: на каждом нажатии недописанная строка стоит на своей
 * позиции, и прежняя недописанная там же.
 */
function assignIds(
  texts: readonly string[],
  previous: readonly TranslateItem[],
  newId: () => string,
): TranslateItem[] {
  const taken = new Set<number>();
  const ids: (string | null)[] = texts.map((text) => {
    const at = previous.findIndex((item, i) => !taken.has(i) && item.text === text);
    if (at < 0) return null;
    taken.add(at);
    return previous[at]?.id ?? null;
  });

  // Позиция что-то значит, только когда строк столько же: правка на месте
  const inPlace = texts.length === previous.length;
  return texts.map((text, i) => {
    const matched = ids[i];
    if (matched !== null && matched !== undefined) return { id: matched, text };
    const same = previous[i];
    if (inPlace && same !== undefined && !taken.has(i)) {
      taken.add(i);
      return { id: same.id, text };
    }
    return { id: newId(), text };
  });
}

/**
 * Вставленный текст в предложения задания (2.2 проекта).
 *
 * `newId` параметром, потому что генератор идентификаторов живёт за барьером
 * `@edu/shared/exercises`, а этот модуль в корне пакета. Функция чистая при
 * чистом `newId`, и тесты подставляют счётчик.
 *
 * Чего разбор НЕ делает намеренно: не режет строку на предложения по точке
 * («Mr. Smith», «т. е.») и не склеивает строки без нумерации. Обе ошибки
 * тихие, а пометка видна.
 */
export function parseTranslateItems(
  text: string,
  previous: readonly TranslateItem[],
  newId: () => string,
): TranslateParse {
  const cleaned = text
    .split(LINE_BREAK)
    .map(cleanLine)
    // Пустая строка без номера выпадает сразу, пустая с номером ждёт склейки
    .filter((line) => line.text !== "" || line.numbered);
  const lines = mergeWrapped(attachBareNumbers(cleaned)).filter((line) => line.text !== "");

  const kept = lines.slice(0, TRANSLATE_MAX_ITEMS);
  const items = assignIds(
    kept.map((line) => line.text),
    previous,
    newId,
  );

  const flags: Record<string, TranslateFlag[]> = {};
  kept.forEach((line, i) => {
    const found = flagsOf(line, kept[i - 1]);
    const item = items[i];
    if (found.length > 0 && item !== undefined) flags[item.id] = found;
  });

  return { items, flags, dropped: lines.length - kept.length };
}
